import React, { useState, useCallback, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Col, Container, Row, Button } from "react-bootstrap";
import usePersistedFilters from "../hooks/usePersistedFilters";
import usePersistedPageSize from "../hooks/usePersistedPageSize";
import DataImportFilters from "../components/DataImport/DataImportFilters";
import DataImportTable from "../components/DataImport/DataImportTable";
import DataImportButtons from "../components/DataImport/DataImportButtons";
import PaginationComponent from "../components/PaginationComponent";
import UploadIcon from "../assets/images/upload_file_rgb_red.svg";
import "../assets/styles/dataImport.scss";
import api from "../services/httpClient";

const DataImport = () => {
  const { accounts } = useMsal();
  const claims = accounts?.[0]?.idTokenClaims || {};
  const rolesRaw = claims.roles || claims.role || claims.groups;
  const roles = Array.isArray(rolesRaw) ? rolesRaw : rolesRaw ? [rolesRaw] : [];
  const isSuperUser = roles.includes("ROLE_SUPERUSER");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize("dataImport", 20);
  const { filters, handleFilterChange, clearFilters, hasActiveFilters } = usePersistedFilters("dataImport");
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sortConfig, setSortConfig] = useState({ key: "StartDate", direction: "desc" });
  const [selectedIds, setSelectedIds] = useState([]);

  const [data, setData] = useState({
    rows: [],
    total: 0,
    totalPages: null,
  });

  const fetchData = useCallback(
    async (
      pageNumber = currentPage,
      pageSize = itemsPerPage,
      filtersToApply = appliedFilters,
      sortConfigToApply = sortConfig
    ) => {
      try {
        const offset = (pageNumber - 1) * pageSize;

        const requestBody = {
          sort: sortConfigToApply.key,
          order: sortConfigToApply.direction,
          offset: offset,
          limit: pageSize,
          search: {
            dateStartFrom: filtersToApply.dateStartFrom || "",
            dateStartTo: filtersToApply.dateStartTo || "",
            rollbackDateFrom: filtersToApply.rollbackDateFrom || "",
            rollbackDateTo: filtersToApply.rollbackDateTo || "",
            status: filtersToApply.status !== undefined && filtersToApply.status !== "" ? Number(filtersToApply.status) : null,
            action: filtersToApply.action !== undefined && filtersToApply.action !== "" ? Number(filtersToApply.action) : null,
            fileName: filtersToApply.fileName || "",
          },
        };

        const response = await api.post(`/HistoryBulk/List`, requestBody);

        const result = response.data?.data || response.data;
        const rows = Array.isArray(result.items) ? result.items : (Array.isArray(result.rows) ? result.rows : []);
        const total = result.itemsCount ?? result.total ?? 0;
        const totalPages = result.totalPages ?? null;
        setData({ rows, total, totalPages });
      } catch (error) {
        console.error("Error fetching data import list:", error);
        setData({ rows: [], total: 0 });
      }
    },
    [currentPage, itemsPerPage, appliedFilters, sortConfig]
  );

  useEffect(() => {
    fetchData();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleImmediateFilterChange = (field, value) => {
    handleFilterChange(field, value);
    const updatedFilters = { ...filters, [field]: value };
    setAppliedFilters(updatedFilters);
    fetchData(1, itemsPerPage, updatedFilters);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    fetchData(1, itemsPerPage, filters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    fetchData(currentPage, itemsPerPage, appliedFilters, newSortConfig);
  };

  const handleSelectionChange = (newSelectedIds) => {
    setSelectedIds(newSelectedIds);
  };

  const handleAccept = async () => {
    if (selectedIds.length === 0) {
      alert("Proszę wybrać co najmniej jeden rekord do akceptacji.");
      return;
    }
    try {
      await api.post(`/HistoryBulk/Accept`, { id: selectedIds });
      alert("Kolejka zadań została zaakceptowana.");
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.message || "Wystąpił błąd podczas akceptacji.";
      alert(msg);
    }
  };

  const handleReject = async () => {
    if (selectedIds.length === 0) {
      alert("Proszę wybrać co najmniej jeden rekord do odrzucenia.");
      return;
    }
    try {
      await api.post(`/HistoryBulk/Reject`, { id: selectedIds });
      alert("Kolejka zadań została odrzucona.");
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.message || "Wystąpił błąd podczas odrzucania.";
      alert(msg);
    }
  };

  return (
    <div className="data-import-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={UploadIcon}
              alt="Data Import Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Import danych
            </h3>
          </Col>
          <Col xs="auto">
            <DataImportButtons
              onSearch={handleSearch}
              onAccept={handleAccept}
              onReject={handleReject}
              selectedIds={selectedIds}
              isSuperUser={isSuperUser}
            />
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <DataImportFilters onFilterChange={handleFilterChange} onSearch={handleSearch} onImmediateFilterChange={handleImmediateFilterChange} filters={filters} isSuperUser={isSuperUser} />
          </Col>
          <Col className="flex-grow-1" />
        </Row>
        <Row className="justify-content-end mb-3">
          <Col xs="auto">
            <Button
              variant={hasActiveFilters ? "success" : "outline-secondary"}
              size="sm"
              className="me-3"
              onClick={() => { clearFilters(); setAppliedFilters({}); fetchData(1, itemsPerPage, {}); setCurrentPage(1); }}
            >
              Wyczyść filtry
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <DataImportTable
              data={data.rows}
              onSortChange={handleSortChange}
              onSelectionChange={handleSelectionChange}
              selectedIds={selectedIds}
            />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={itemsPerPage}
            dataCount={data.rows.length}
            totalCount={data.total}
            totalPages={data.totalPages}
            pageNumber={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              fetchData(page, itemsPerPage);
            }}
            onItemsPerPageChange={(pageSize) => {
              const size = Number(pageSize);
              setItemsPerPage(size);
              setCurrentPage(1);
              fetchData(1, size);
            }}
            itemsPerPageOptions={[20, 100, 500, 1000]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default DataImport;
